import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/unsubscribe")({
  component: UnsubscribePage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
});

function UnsubscribePage() {
  const { token } = Route.useSearch();
  const [state, setState] = useState<
    "checking" | "valid" | "invalid" | "already" | "submitting" | "done" | "error"
  >("checking");

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.valid) setState("valid");
        else if (data.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      })
      .catch(() => setState("error"));
  }, [token]);

  const onConfirm = async () => {
    setState("submitting");
    try {
      const res = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (data.success) setState("done");
      else if (data.reason === "already_unsubscribed") setState("already");
      else setState("error");
    } catch {
      setState("error");
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 shadow-sm text-center">
        <h1 className="text-2xl font-bold text-foreground mb-4">Unsubscribe</h1>
        {state === "checking" && <p className="text-muted-foreground">Checking your link…</p>}
        {state === "valid" && (
          <>
            <p className="text-muted-foreground mb-6">
              Click below to stop receiving emails from Colorado Web Impressions.
            </p>
            <button
              onClick={onConfirm}
              className="bg-primary text-primary-foreground px-6 py-2 rounded font-semibold hover:opacity-90"
            >
              Confirm unsubscribe
            </button>
          </>
        )}
        {state === "submitting" && <p className="text-muted-foreground">Processing…</p>}
        {state === "done" && (
          <p className="text-foreground">You've been unsubscribed. Sorry to see you go.</p>
        )}
        {state === "already" && (
          <p className="text-foreground">You're already unsubscribed.</p>
        )}
        {state === "invalid" && (
          <p className="text-destructive">This unsubscribe link is invalid or expired.</p>
        )}
        {state === "error" && (
          <p className="text-destructive">Something went wrong. Please try again later.</p>
        )}
      </div>
    </main>
  );
}
